import { useEffect, useState } from "react";
import type { RecurrenceSpec, Series, SeriesScope, TaskId, TaskPatch, TaskView } from "../api/generated";
import { recurrenceCommand } from "../domain/commands";
import { allProjectPaths } from "../domain/projects";
import type { Workspace } from "../state/useWorkspace";
import { Dialog } from "./Dialog";
import { DependencyField } from "./DependencyField";
import { CommitField, DateField, PriorityField, ProjectField, TagField } from "./fields";
import { RecurrenceEditor } from "./RecurrenceEditor";
import { SeriesHistory } from "./SeriesHistory";

interface DetailPaneProps {
  workspace: Workspace;
  taskId: TaskId;
  now: Date;
  onClose(): void;
  onOpenTag(tag: string): void;
}

export function DetailPane({ workspace, taskId, now, onClose, onOpenTag }: DetailPaneProps) {
  const { api, tasks, actions, toasts } = workspace;
  const [view, setView] = useState<TaskView | undefined>(undefined);
  const [series, setSeries] = useState<Series | undefined>(undefined);
  const [scope, setScope] = useState<SeriesScope>("ThisInstance");
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void api.get(taskId).then(async (found) => {
      if (cancelled) return;
      setView(found);
      const seriesId = found?.task.series?.series;
      setSeries(seriesId === undefined ? undefined : await api.series(seriesId));
    });
    return () => {
      cancelled = true;
    };
  }, [api, taskId, tasks]);

  if (view === undefined) {
    return (
      <aside className="detail" aria-label="Task details">
        <button type="button" className="icon-button" aria-label="Close details" onClick={onClose}>
          ×
        </button>
        <p className="empty">This task is no longer available.</p>
      </aside>
    );
  }

  const { task } = view;
  const inSeries = task.series !== undefined;
  const deleted = view.status === "Deleted";
  const edit = (patch: TaskPatch) => void actions.edit(task.id, patch, scope);
  const applyRecurrence = (change: RecurrenceSpec | "Stop") =>
    void actions.run(recurrenceCommand(task.id, change), "Updated repeat");

  return (
    <aside className="detail" aria-label="Task details">
      <div className="detail-header">
        <button
          type="button"
          role="checkbox"
          aria-checked={view.status === "Done"}
          aria-label={view.status === "Done" ? "Mark as not done" : "Mark as done"}
          className="check"
          disabled={deleted}
          onClick={() => void (view.status === "Done" ? actions.reopen(view) : actions.complete(view))}
        >
          ✓
        </button>
        <span className="field-hint" style={{ flex: 1 }}>
          {view.status}
        </span>
        <button type="button" className="icon-button" aria-label="Close details" onClick={onClose}>
          ×
        </button>
      </div>

      <CommitField label="Title" value={task.title} onCommit={(title) => edit({ title })} />
      <CommitField
        label="Notes"
        value={task.notes}
        multiline
        placeholder="Add notes"
        onCommit={(notes) => edit({ notes })}
      />

      {inSeries && (
        <fieldset className="field" style={{ border: 0, padding: 0, margin: "0 0 14px" }}>
          <legend className="field-label">Apply changes to</legend>
          <label>
            <input
              type="radio"
              name="series-scope"
              checked={scope === "ThisInstance"}
              onChange={() => setScope("ThisInstance")}
            />{" "}
            This instance
          </label>
          <label>
            <input
              type="radio"
              name="series-scope"
              checked={scope === "ThisAndFuture"}
              onChange={() => setScope("ThisAndFuture")}
            />{" "}
            This and future instances
          </label>
        </fieldset>
      )}

      <div className="field-row">
        <DateField
          label="Due"
          when={task.due}
          now={now}
          api={api}
          onChange={(due) => edit({ due })}
          onProblem={(message) => toasts.show(message, { tone: "error" })}
        />
        <DateField
          label="Wait until"
          when={task.wait}
          now={now}
          api={api}
          onChange={(wait) => edit({ wait })}
          onProblem={(message) => toasts.show(message, { tone: "error" })}
        />
      </div>
      <div className="field-row">
        <PriorityField priority={task.priority} onChange={(priority) => edit({ priority })} />
      </div>
      <ProjectField
        project={task.project}
        knownPaths={allProjectPaths(workspace.projects)}
        onChange={(project) => edit({ project })}
      />
      <TagField
        tags={task.tags}
        knownTags={workspace.tags.map(({ tag }) => tag)}
        onOpen={onOpenTag}
        onAdd={(tag) => edit({ add_tags: [tag] })}
        onRemove={(tag) => edit({ remove_tags: [tag] })}
      />
      <DependencyField
        api={api}
        taskId={task.id}
        dependencies={task.depends_on}
        blockedBy={view.blocked_by}
        revision={tasks}
        onAdd={(id) => edit({ add_deps: [id] })}
        onRemove={(id) => edit({ remove_deps: [id] })}
      />

      <RecurrenceEditor key={task.id} series={series} onApply={applyRecurrence} />
      {task.series !== undefined && (
        <SeriesHistory
          api={api}
          seriesId={task.series.series}
          currentId={task.id}
          now={now}
          revision={tasks}
          onSelect={workspace.select}
        />
      )}

      <p className="field-hint">
        Added {new Date(task.added).toLocaleString()} · Modified {new Date(task.modified).toLocaleString()}
      </p>

      <div className="detail-actions">
        {deleted ? (
          <button type="button" className="button" onClick={() => void actions.restore(view)}>
            Restore
          </button>
        ) : (
          <button
            type="button"
            className="button danger"
            onClick={() => {
              if (inSeries) setConfirmingDelete(true);
              else void actions.remove(view, "ThisInstance");
            }}
          >
            Delete
          </button>
        )}
      </div>

      {confirmingDelete && (
        <Dialog title="Delete recurring task" onClose={() => setConfirmingDelete(false)}>
          <p>“{task.title}” repeats. What should be deleted?</p>
          <div className="dialog-actions">
            <button type="button" className="button" onClick={() => setConfirmingDelete(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="button danger"
              onClick={() => {
                setConfirmingDelete(false);
                void actions.remove(view, "ThisInstance");
              }}
            >
              This instance
            </button>
            <button
              type="button"
              className="button danger"
              onClick={() => {
                setConfirmingDelete(false);
                void actions.remove(view, "ThisAndFuture");
              }}
            >
              The whole series
            </button>
          </div>
        </Dialog>
      )}
    </aside>
  );
}
